// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import {
  BaseDatePicker,
  BaseLabel,
  BaseSelect,
  BaseStatus,
  BaseTable,
  Loading,
  UnderDevelopment,
} from '~/components/common';
// Others
import { IFinancialOrderReport, IQueryParamListExport } from '~/utils/interface/reports';
import {
  BACK_TO_PREVIOUS_PAGE,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_VALUE,
  optionViewReport,
} from '~/utils/constants/common';
import { IBaseOption, IHeaderCellType, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { formatCurrencyUSD, formatDate, getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { DateFormatEnum, ReportTypeEnum, RoleEnum, TypeMasterDataListEnum } from '~/utils/enum/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { getListUser } from '~/thunks/user/userThunk';
import { IQueryParamUser, IUser } from '~/utils/interface/user';
import { IMasterData, IQueryMasterData } from '~/utils/interface/masterData';
import { getListMasterData } from '~/thunks/masterData/masterDataThunk';
import { exportDataReport, getListFinancialOrderReport } from '~/thunks/reports/reportsThunk';
// Styles, images, icons
import styles from './ReportsFinancialOrder.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IFinancialOrderReport>[] => {
  return [
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_status')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => (record?.status ? <BaseStatus label={record?.status} /> : EMPTY_VALUE),
    },
    {
      id: 'orderNumber',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_order')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return <Typography className={cx('textQty')}>{record?.orderNumber || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'createdAt',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_invoice')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return record?.invoice?.[0]?.invoiceNumber || EMPTY_VALUE;
      },
    },
    {
      id: 'salesPerson',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_sales_person')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return record?.salesPerson
          ? getFullName({
              firstName: record?.salesPerson?.firstName,
              lastName: record?.salesPerson?.lastName,
            })
          : EMPTY_VALUE;
      },
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_client')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return getFullName({ firstName: record?.client?.firstName, lastName: record?.client?.lastName });
      },
    },
    {
      id: 'createdAt',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_date_created')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return formatDate(record?.createdAt, DateFormatEnum.M_D_YYYY);
      },
    },
    {
      id: 'startDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_start_date')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return formatDate(record?.startDate, DateFormatEnum.M_D_YYYY);
      },
    },
    {
      id: 'endDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_end_date')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return formatDate(record?.endDate, DateFormatEnum.M_D_YYYY);
      },
    },
    {
      id: 'eventType',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_event_type')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return record?.eventType?.name || EMPTY_VALUE;
      },
    },
    {
      id: 'total',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_total')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return (
          <Typography className={cx('textQty')}>
            {formatCurrencyUSD({ value: record?.total, showDecimal: true })}
          </Typography>
        );
      },
    },
    {
      id: 'totalWithoutTax',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_taxable_total')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return (
          <Typography className={cx('textQty')}>
            {formatCurrencyUSD({ value: record?.totalWithoutTax, showDecimal: true })}
          </Typography>
        );
      },
    },
    {
      id: 'discounted',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('reports_table_discounted')}</Box>
        </Stack>
      ),
      render: (record: IFinancialOrderReport) => {
        return (
          <Stack direction={'row'} justifyContent={'center'}>
            <Typography className={cx('textSubrental')}>
              {formatCurrencyUSD({ value: record?.discounted, showDecimal: true })}
            </Typography>
          </Stack>
        );
      },
    },
  ];
};
const ReportsFinancialOrder = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [financialOrderList, setFinancialOrderList] = useState<IFinancialOrderReport[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    view: 'month',
    startDate: moment(new Date()).format(DateFormatEnum.YYYY_MM_DD),
    endDate: moment(new Date()).format(DateFormatEnum.YYYY_MM_DD),
  });
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListFinancialOrder();
  }, [paramsObject.page]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListFinancialOrder = () => {
    setIsLoading(true);

    dispatch(getListFinancialOrderReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses, pagination } = res.data;

        setFinancialOrderList(responses);
        setPagination(pagination);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListFinancialOrder();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.FINANCIAL_ORDER,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const fetchOptionsUser = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;
    const payloadParams: IQueryParamUser = {
      page: page,
      limit: DEFAULT_LIMIT_PAGE,
      role: RoleEnum.SALES_PERSON,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses: listCategory, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = handleConvertOptionUser(listCategory);

      const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

      return { optionsResp: formatOptions, hasMoreResp: hasMore };
    } catch (error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionsView = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    try {
      return { optionsResp: optionViewReport, hasMoreResp: false };
    } catch (error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const handleConvertOptionUser = (data: IUser[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: getFullName({ firstName: item.firstName, lastName: item.lastName }),
    }));

    return options;
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamsObject({ ...paramsObject, page });
    setSearchParams({ ...params, page: String(page) });
  };

  const fetchOptionsEventType = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;
      const payloadParams: IQueryMasterData = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        type: TypeMasterDataListEnum.EVENT,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListMasterData(payloadParams)).unwrap();

        const { responses: listEventType, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = handleConvertOptionEventType(listEventType);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertOptionEventType = (data: IMasterData[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    return options;
  };
  //#endregion Handle Function

  return (
    <Stack className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_financial_order_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_financial_order_desc_page')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'start'} rowSpacing={'24px'} container>
          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
            <BaseDatePicker
              mode='date'
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => setParamsObject((prev) => ({ ...prev, startDate: date as string }))}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
            <BaseDatePicker
              mode='date'
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => setParamsObject((prev) => ({ ...prev, endDate: date as string }))}
            />
          </Grid2>

          <Grid2 size={4} />

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
            <BaseSelect
              isSearch
              gap={8}
              type='loadMore'
              styleType='contained'
              label={t('reports_financial_order_user_label')}
              value={paramsObject.accountId}
              callApiFn={fetchOptionsUser}
              onChange={(data) => setParamsObject((prev) => ({ ...prev, accountId: data?.value }))}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
            <BaseSelect
              gap={8}
              styleType='contained'
              label={t('reports_financial_order_group_by')}
              value={paramsObject.view}
              callApiFn={fetchOptionsView}
              onChange={(data) => setParamsObject((prev) => ({ ...prev, view: data?.value }))}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
            <BaseSelect
              isSearch
              gap={8}
              styleType='contained'
              label={t('reports_financial_order_event_type')}
              callApiFn={fetchOptionsEventType}
              value={paramsObject.eventTypeId}
              type='loadMore'
              onChange={(data) => setParamsObject((prev) => ({ ...prev, eventTypeId: data?.value }))}
            />
          </Grid2>
        </Grid2>

        <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            disabled={
              !paramsObject.startDate &&
              !paramsObject.eventTypeId &&
              !paramsObject.endDate &&
              !paramsObject.accountId &&
              !paramsObject.view
            }
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            disabled={!financialOrderList.length}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Box className={cx('tableContainer')}>
        <BaseTable
          dataSource={financialOrderList}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ReportsFinancialOrder;
